import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import React from "react";
import { getStatusField } from "../../../util/src/index";
import {
  AutoField,
  LiftField,
  LiftStatus,
  OverturnColorField,
  OverturnField,
  RefereeColorField,
} from "../../../util/src/types";
import RenderRecord from "../misc/RenderRecord";
import { LiftCellProps } from "./types";
import Shape from "../misc/Shape";
import ReplayIcon from "@mui/icons-material/Replay";
import dico from "../../../util/src/language/dico";
/**
 * Component for displaying lift data in a table cell with proper styling based on lift status
 */
const LiftCell = ({
  athlete,
  field,
  records,
  isMax = false,
  onHypotheticalRankClick,
}: LiftCellProps) => {
  const liftField = field as keyof typeof athlete;
  const statusField = getStatusField(field) as keyof typeof athlete;
  const status = athlete[statusField] as LiftStatus;
  const value = athlete[liftField] as number;
  const autoKey = `${field}Auto` as AutoField;
  const overturnKey = `${field}Overturn` as OverturnField;
  const leftColorKey = `${field}LeftColor` as RefereeColorField;
  const chiefColorKey = `${field}ChiefColor` as RefereeColorField;
  const rightColorKey = `${field}RightColor` as RefereeColorField;
  const overturnColorKey = `${field}OverturnColor` as OverturnColorField;
  const isAuto = athlete[autoKey];
  const isOverturn = athlete[overturnKey];
  const isFirstCell =
    field === "squat1" || field === "bench1" || field === "deadlift1";

  const lightsKeys: Array<{
    liftStatus: LiftStatus;
    value: number;
    lift: number;
    leftColor: string;
    rightColor: string;
    chiefColor: string;
    overturnColor: string;
    isAuto: boolean;
    isOverturn: boolean;
  }> = [];

  if (isMax) {
    for (let i = 1; i <= 3; i++) {
      lightsKeys.push({
        value: athlete[
          field
            .replace("2", i.toString())
            .replace("3", i.toString())
            .replace("2", i.toString()) as keyof typeof athlete
        ] as number,
        liftStatus: athlete[
          `${field}Status`
            .replace("2", i.toString())
            .replace("3", i.toString())
            .replace("2", i.toString()) as keyof typeof athlete
        ] as LiftStatus,
        lift: i,
        isAuto:
          athlete[
            `${field}Auto`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as AutoField
          ] ?? false,
        isOverturn:
          athlete[
            `${field}Overturn`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as OverturnField
          ] ?? false,
        chiefColor:
          athlete[
            `${field}ChiefColor`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as RefereeColorField
          ] ?? "",
        leftColor:
          athlete[
            `${field}LeftColor`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as RefereeColorField
          ] ?? "",
        overturnColor:
          athlete[
            `${field}OverturnColor`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as OverturnColorField
          ] ?? "",
        rightColor:
          athlete[
            `${field}RightColor`
              .replace("2", i.toString())
              .replace("3", i.toString())
              .replace("2", i.toString()) as RefereeColorField
          ] ?? "",
      });
    }
  }

  // Determine cell styling based on lift status
  let cellStyle: React.CSSProperties = {};

  switch (status) {
    case LiftStatus.ANNONCED:
      // Add white semi-transparent overlay for announced lifts
      cellStyle = {
        backgroundColor: value ? "rgba(255, 255, 255, 0.2)" : "inherit",
      };
      break;
    case LiftStatus.LIFT:
      // Green background for successful lifts (except max display)
      cellStyle = {
        backgroundColor: isMax ? "inherit" : "rgb(3, 255, 0)",
      };
      break;
    case LiftStatus.NO_LIFT:
      // Blue background with strikethrough for failed lifts
      cellStyle = {
        backgroundColor: "rgb(192,192,255)",
        textDecoration: "line-through",
        padding: "0px",
      };
      break;
    default:
      cellStyle = {};
      break;
  }

  const cellContent = (
    <Tooltip
      title={
        isMax && isAuto && lightsKeys.length > 0 ? (
          <React.Fragment>
            <Box>
              {lightsKeys.map((l, index) => {
                if (l.isAuto) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1em",
                        alignItems: "center",
                      }}
                      key={index}
                    >
                      <Typography sx={{ color: "white" }}>
                        {dico().attempt + " " + l.lift + " : "}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "1px",
                        }}
                      >
                        <Shape
                          variant="circle"
                          size={10}
                          color={l.leftColor}
                          fill={l.leftColor}
                        />
                        <Shape
                          variant="circle"
                          size={10}
                          color={l.chiefColor}
                          fill={l.chiefColor}
                        />
                        <Shape
                          variant="circle"
                          size={10}
                          color={l.rightColor}
                          fill={l.rightColor}
                        />
                        {l.isOverturn && (
                          <ReplayIcon
                            sx={{ color: l.overturnColor, fontSize: "15px" }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          color:
                            l.liftStatus == LiftStatus.LIFT
                              ? "rgb(3, 255, 0)"
                              : "red",
                        }}
                      >
                        {"(" + l.value + ")"}
                      </Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          </React.Fragment>
        ) : (
          ""
        )
      }
    >
      <Box
        sx={{
          ...cellStyle,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          // Compensate for parent cell padding
          margin: "-8px -4px",
          // Restore same visual spacing
          padding: "8px 4px",
          borderLeft: isFirstCell || isMax ? "1px solid black" : "none",
        }}
      >
        {isOverturn && !isMax && (
          <Box sx={{ position: "absolute", top: "0", left: "0" }}>
            <ReplayIcon
              sx={{ color: athlete[overturnColorKey] || "", fontSize: "15px" }}
            />
          </Box>
        )}
        <RenderRecord
          athlete={athlete}
          lift={field as LiftField}
          total={false}
          weight={value}
          records={records}
        />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                  md: "0.9rem",
                  lg: "1.2rem",
                },
                textAlign: "center",
              }}
            >
              {value || ""}
            </Typography>
            {/* Hypothetical Powerscout rank - current attempts and collapsed max of other lifts */}
            {Boolean(value) && value > 0 && onHypotheticalRankClick && (
              <Tooltip title={dico().powerscout.hypotheticalRankTooltip}>
                <IconButton
                  size="small"
                  aria-label={dico().powerscout.hypotheticalRankTooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onHypotheticalRankClick({ athlete, field, value });
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    p: 0.15,
                    ml: 0.25,
                    color: "text.secondary",
                    "& .MuiSvgIcon-root": { fontSize: "0.85rem" },
                  }}
                >
                  <LeaderboardOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {isAuto && !isMax && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: "3px" }}>
              <Shape
                variant="circle"
                size={10}
                color={athlete[leftColorKey] || ""}
                fill={athlete[leftColorKey] || ""}
              />
              <Shape
                variant="circle"
                size={10}
                color={athlete[chiefColorKey] || ""}
                fill={athlete[chiefColorKey] || ""}
              />
              <Shape
                variant="circle"
                size={10}
                color={athlete[rightColorKey] || ""}
                fill={athlete[rightColorKey] || ""}
              />
            </Box>
          )}
        </Box>
        {!value && (
          <div
            style={{
              color:
                status !== LiftStatus.ANNONCED ? "rgb(192,192,255)" : "inherit",
              textDecoration:
                status === LiftStatus.NO_LIFT ? "line-through" : "none",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                status === LiftStatus.NO_LIFT ? "rgb(192,192,255)" : "inherit",
            }}
          >
            {status === LiftStatus.NO_LIFT ? "x" : ""}
          </div>
        )}
      </Box>
    </Tooltip>
  );

  return cellContent;
};

export default LiftCell;
